import { ComponentProps, HTMLAttributes, ImgHTMLAttributes } from "react";
import { BlockTitle, BlockTitleProps } from "@blocks/Blocks";
import Column from "@components/Grid/Column";
import Row from "@components/Grid/Row";
import { useWindowSize } from "@hooks/useWindowSize";
import Card from "@particles/card/Card";
import { CardVariations } from "@particles/card/utils/types";
import { cn } from "@util/helpers";

export type EventsAwardsPageRowProps = {
  /**LogoAttributes to show the logo on the events awards page */
  logoAttributes: ImgHTMLAttributes<HTMLImageElement>;
  /**BlockTitleProps to show the title of the events awards page */
  blockTitleProps: BlockTitleProps;
  /**Description to show the information events awards */
  description: string;
  /**Article data to show the article card on events awards page */
  articleProps: ComponentProps<typeof Card>;
} & Pick<HTMLAttributes<HTMLDivElement>, "className">;

const EventsAwardsPageRow = ({
  logoAttributes,
  articleProps,
  blockTitleProps,
  description,
  className,
}: EventsAwardsPageRowProps): React.ReactElement => {
  const { isScreenLG } = useWindowSize();

  const variant: CardVariations = {
    image: {
      position: "",
      width: "w-full",
      aspectRatio: "aspect-3x2",
    },
    content: { width: "w-full", extraClass: "pt-2 text-gray-850" },
    title: {
      size: "text-base",
      color: "text-gray-850",
      extraClass: "font-lct",
    },
  };

  return (
    <Row rootClassName={cn("py-6", className)}>
      <Column rootClassName="w-full mb-5 lg:w-6/12 lg:order-2 xl:w-4/12">
        <BlockTitle
          {...blockTitleProps}
          rootClassName={`${blockTitleProps.rootClassName} tracking-[0.075rem] uppercase`}
        />

        <p className="font-public-sans text-lg leading-6 text-gray-850">
          {description}
        </p>
      </Column>

      <Column rootClassName="w-full lg:w-3/12 lg:order-1 xl:w-4/12">
        <img {...logoAttributes} className="mx-auto" />
      </Column>

      <>
        {isScreenLG ? (
          <Column rootClassName="w-full lg:w-3/12 lg:order-3 xl:w-4/12">
            <Card {...articleProps} variations={variant} />
          </Column>
        ) : null}
      </>
    </Row>
  );
};

export default EventsAwardsPageRow;
